// @services/clients/DocumentAssistantDictationClient.ts
import { v7 as uuidv7 } from "uuid";
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import API_URL from "@utils/config";
import type {
    DictationApiDecodeResult,
    DictationError,
    DictationPayloadValue,
    DictationTraceId,
    DictationTranscriptionRequest,
    DictationTranscriptionResponse,
} from "@services/clients/DocumentAssistantDictationApi";
import {
    decodeDictationTranscriptionRefusal,
    decodeDictationTranscriptionRequest,
    decodeDictationTranscriptionResponse,
    dictationError,
    DocumentAssistantDictationContract,
    makeDictationTraceId,
} from "@services/clients/DocumentAssistantDictationApi";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export interface DocumentAssistantDictationClient {
    readonly transcribeAudio: (
        request: DictationTranscriptionRequest,
    ) => TaskEitherTs<DictationError, DictationTranscriptionResponse>;
}

type DictationResponseDecoder<T> = (payload: DictationPayloadValue) => DictationApiDecodeResult<T>;

const requestDecodeError = (request: DictationTranscriptionRequest): DictationError =>
    dictationError(
        "emptyRecording",
        "Dictation audio capture is invalid.",
        Option.some(request.capture.fieldId),
        Option.none(),
    );

const responseDecodeError = (
    request: DictationTranscriptionRequest,
    traceId: string,
): DictationError =>
    dictationError(
        "responseDecodeError",
        "Unable to decode the transcription response.",
        Option.some(request.capture.fieldId),
        decodeTraceId(traceId),
    );

const networkError = (request: DictationTranscriptionRequest): DictationError =>
    dictationError(
        "networkError",
        "The dictation transcription service could not be reached.",
        Option.some(request.capture.fieldId),
        Option.none(),
    );

const decodeTraceId = (traceId: string): OptionTs<DictationTraceId> => {
    const decoded = makeDictationTraceId(traceId);
    return decoded.type === "ok" ? Option.some(decoded.value) : Option.none<DictationTraceId>();
};

const statusError = (
    status: number,
    request: DictationTranscriptionRequest,
    traceId: string,
): DictationError => {
    switch (status) {
        case 400:
        case 413:
        case 415:
            return dictationError(
                "uploadFailed",
                "The dictation upload was rejected.",
                Option.some(request.capture.fieldId),
                decodeTraceId(traceId),
            );
        case 401:
        case 403:
            return dictationError(
                "authRequired",
                "Sign in again before using dictation.",
                Option.some(request.capture.fieldId),
                decodeTraceId(traceId),
            );
        case 503:
            return dictationError(
                "transcriptionUnavailable",
                "The transcription service is unavailable.",
                Option.some(request.capture.fieldId),
                decodeTraceId(traceId),
            );
        default:
            return dictationError(
                status >= 500 ? "transcriptionUnavailable" : "uploadFailed",
                "The dictation request failed.",
                Option.some(request.capture.fieldId),
                decodeTraceId(traceId),
            );
    }
};

const errorFromResponseBody = (
    status: number,
    bodyText: string,
    request: DictationTranscriptionRequest,
    traceId: string,
): DictationError => {
    if (bodyText.trim().length === 0) {
        return statusError(status, request, traceId);
    }

    try {
        const decoded = decodeDictationTranscriptionRefusal(JSON.parse(bodyText));
        return decoded.type === "ok" ? decoded.value : statusError(status, request, traceId);
    } catch {
        return responseDecodeError(request, traceId);
    }
};

const decodeOrLeft = <T>(
    result: DictationApiDecodeResult<T>,
    decodeError: DictationError,
): TaskEitherTs<DictationError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value);
    }

    return taskEitherLeft(decodeError);
};

const readResponseJson = async (response: Response): Promise<DictationPayloadValue> => response.json();
const readResponseText = async (response: Response): Promise<string> => response.text();

const mapFetchResponse = <T>(
    response: Response,
    request: DictationTranscriptionRequest,
    traceId: string,
    decoder: DictationResponseDecoder<T>,
): TaskEitherTs<DictationError, T> => {
    if (!response.ok) {
        const bodyTask = taskEitherTryCatch(
            () => readResponseText(response),
            () => responseDecodeError(request, traceId),
        );

        return taskEitherChain(bodyTask, (bodyText) =>
            taskEitherLeft(errorFromResponseBody(response.status, bodyText, request, traceId)),
        );
    }

    const payloadTask = taskEitherTryCatch(
        () => readResponseJson(response),
        () => responseDecodeError(request, traceId),
    );

    return taskEitherChain(payloadTask, (payload) =>
        decodeOrLeft(decoder(payload), responseDecodeError(request, traceId)),
    );
};

const multipartBody = (request: DictationTranscriptionRequest): FormData => {
    const body = new FormData();
    body.append("file", request.capture.blob, `dictation.${request.capture.extension}`);
    body.append("extension", request.capture.extension);
    body.append("mimeType", request.capture.mimeType);
    body.append("fieldId", request.capture.fieldId);
    body.append("durationMillis", String(request.capture.durationMillis));
    return body;
};

const fetchTranscription = (
    request: DictationTranscriptionRequest,
): TaskEitherTs<DictationError, DictationTranscriptionResponse> => {
    const traceId = uuidv7();
    const fetchTask = taskEitherTryCatch(
        () =>
            fetch(`${API_URL}${DocumentAssistantDictationContract.transcriptionRoute}`, {
                method: "POST",
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "X-Trace-ID": traceId,
                },
                body: multipartBody(request),
            }),
        () => networkError(request),
    );

    return taskEitherChain(fetchTask, (response) =>
        mapFetchResponse(response, request, traceId, decodeDictationTranscriptionResponse),
    );
};

export class DefaultDocumentAssistantDictationClient implements DocumentAssistantDictationClient {
    transcribeAudio(
        request: DictationTranscriptionRequest,
    ): TaskEitherTs<DictationError, DictationTranscriptionResponse> {
        const decodedRequest = decodeDictationTranscriptionRequest({ capture: request.capture });
        if (decodedRequest.type === "error") {
            return taskEitherLeft(requestDecodeError(request));
        }

        return fetchTranscription(decodedRequest.value);
    }
}
